import React from "react";
import { environment } from "../../config/environment";
import { Box } from "../../atoms/Layout/Box/Box";
import { GameEndScore } from "./GameEndScore";
import LocalScoreService from "../../services/LocalScoreService";
import styles from "./GameEnd.css";
import { Analytics } from "../../services/Analytics/Analytics";
import { Game } from "../../models/Game";
import { MarkersKeys } from "../../services/MeasureService";
import { isNotPc } from "../../services/DeviceDetector";
import { isMobileDevice } from "../../../utils";
import { GameEndWidget } from "./GameEndWidget";
import { CrossPromotionProps, CrossPromotion } from "./CrossPromotion";
import { EngagementPromoProps, EngagementPromo } from "./EngagementPromo";
import { Sharer } from "./Sharer";
import { Fa } from "../../atoms/Icons/Fa";

type GameEndProps = {
  score: number;
  game: Game;
  highScore: number;
  isUserAuthorized: boolean;
  isGameScoreHacked: boolean;
  isSaveScoreToLeaderboard: boolean;
  crossPromotion: Partial<CrossPromotionProps>;
  engagementPromo: Partial<EngagementPromoProps>;
};

type GameEndState = {
  gameTopScores: number[];
  isSharePopupOpen: boolean;
};

const GAME_END_SHARE_TEXTS = {
  CHAMP: "I am a Champ today! ⭐⭐⭐⭐⭐",
  WINNER: "I am a Winner today! ⭐⭐⭐⭐",
  ACHIEVER: "I am an Achiever today! ⭐⭐⭐",
  GENERAL_HIGHER: "What a fun and challenging game! ⭐⭐",
  GENERAL: "What a fun and challenging game! ⭐",
};

export class GameEnd extends React.Component<GameEndProps, GameEndState> {
  state: GameEndState = {
    gameTopScores: [],
    isSharePopupOpen: false,
  };

  componentDidMount() {
    Analytics.trackEvent(Analytics.games.playAgainImpressions(this.props.game));
    this.scrollWindowToTop(); // scrolling to top if user has 0 points
    this.setGameTopScores();
  }

  componentDidUpdate(prevProps: GameEndProps) {
    if (prevProps.game !== this.props.game) {
      this.setGameTopScores();
    }
    this.scrollWindowToTop(); // scrolling to top after user scores are loaded
  }

  formatScore(score) {
    const value = score || 0;
    return value.toLocaleString(navigator.language, { minimumFractionDigits: 0 });
  }

  private scrollWindowToTop() {
    if (isMobileDevice()) {
      Promise.resolve().then(() => {
        window.scrollTo(0, 0);

        // fix of scroll to top on game end on mobiles issue,
        // each time when user scrolls a page, it jumps to a page's top
        window.scrollTo = function () {
          return;
        };
      });
    }
  }

  private setGameTopScores() {
    const gameTopScores = this.props.game.seo_topPlayerScores.map((score) =>
      parseInt(score)
    );
    gameTopScores.sort((a, b) => b - a);
    this.setState({ gameTopScores });
  }

  onPlayAgainClick = () => {
    performance.mark(MarkersKeys.PlayAgainClick);

    Analytics.trackEvent(Analytics.games.playAgainClick(this.props.game));
    window.location.reload();
  };

  onLoginClick = () => {
    const { isGameScoreHacked, game, score } = this.props;
    if (!isGameScoreHacked) {
      LocalScoreService.initLoginToSaveScore({
        slug: game.slug,
        value: score,
      });
    } else {
      LocalScoreService.redirectToLogin();
    }
  };

  onCrossPromotionClick = () => {
    Analytics.trackEvent(
      Analytics.games.gameEndCrossPromotionClick(this.props.game)
    );
  };

  onEngagementPromoClick = () => {
    Analytics.trackEvent(
      Analytics.games.gameEndEngagementPromoClick(this.props.game)
    );
  };

  getShareTextAffirmation = () => {
    const { score } = this.props;
    const { gameTopScores } = this.state;
    const [champScore, winnerScore, achieverScore] = gameTopScores;

    if (score >= champScore) return GAME_END_SHARE_TEXTS.CHAMP;
    if (score >= winnerScore) return GAME_END_SHARE_TEXTS.WINNER;
    if (score >= achieverScore) return GAME_END_SHARE_TEXTS.ACHIEVER;

    if (achieverScore && score > achieverScore / 2) {
      return GAME_END_SHARE_TEXTS.GENERAL_HIGHER;
    }

    return GAME_END_SHARE_TEXTS.GENERAL;
  };

  getShareText = () => {
    const { game, score } = this.props;
    const { gameTopScores } = this.state;

    if (gameTopScores.length < 3) return;

    const message = this.getShareTextAffirmation();

    return `
      I just scored ${this.formatScore(score)} 
      playing ${game.name} on AARP Games. ${message} 
      ${game.seo_FinalURL}
    `;
  };

  onShare = async () => {
    Analytics.trackEvent(Analytics.games.gameEndShareClick(this.props.game));

    const navigator: any = window.navigator;
    if (isNotPc() && "canShare" in navigator) {
      try {
        await navigator.share({ text: this.getShareText() });
      } catch (err) {
        console.error("Error sharing content:", err);
      }
    } else {
      this.setState({ isSharePopupOpen: true });
    }
  };

  render() {
    const {
      isUserAuthorized,
      isGameScoreHacked,
      score,
      highScore,
      crossPromotion,
      engagementPromo,
    } = this.props;
    const BOX_MAX_LENGHT = 80;

    const { gameTopScores } = this.state;

    return (
      <Container>
        {!!this.getShareText() && (
          <>
            <ShareButton onClick={this.onShare} aria-label="Share your score">
              <Fa icon="share" />
              Share your score
            </ShareButton>
            <Sharer
              text={this.getShareText()}
              isOpen={this.state.isSharePopupOpen}
              onClose={() => this.setState({ isSharePopupOpen: false })}
            />
          </>
        )}
        <Box
          data-element-description="game-end"
          className={styles.gameEndWrapper}
          direction="column"
          justify="center"
        >
          <GameEndWidget score={score} percentiles={gameTopScores} />
          <GameEndScore
            score={score}
            highScore={highScore}
            isUserAuthorized={isUserAuthorized}
            onLoginClick={this.onLoginClick}
            onPlayAgainClick={this.onPlayAgainClick}
            isGameScoreHacked={isGameScoreHacked}
          />
        </Box>

        <div className={styles.promoWrapper}>
          {engagementPromo && (
            <EngagementPromo
              htmlText={engagementPromo.htmlText}
              link={engagementPromo.link}
              onClick={this.onEngagementPromoClick}
              imageSrc={engagementPromo.imageSrc}
              mobileImageSrc={engagementPromo.mobileImageSrc}
              showImages={engagementPromo.showImages}
              maxTextLength={BOX_MAX_LENGHT}
            />
          )}

          {crossPromotion && (
            <CrossPromotion
              htmlText={crossPromotion.htmlText}
              imageSrc={crossPromotion.imageSrc}
              mobileImageSrc={crossPromotion.mobileImageSrc}
              link={crossPromotion.link}
              onClick={this.onCrossPromotionClick}
              showImages={crossPromotion.showImages}
              maxTextLength={BOX_MAX_LENGHT}
            />
          )}
        </div>
      </Container>
    );
  }
}

const Container = (props: any) => (
  <div className={styles.gameEndContainer} {...props} />
);

const ShareButton = (props: any) => (
  <button className={styles.gameEndShareButton} {...props} />
);
